import { forwardRef, useEffect, useState } from 'react';

// material-ui
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Slide,
    TextField
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// project imports
import { gridSpacing, PAYMENT_STATUS, BOOKING_STATUS, USER_TYPE, PAYMENT_TYPE, PAYMENT_MODE, DELETED_STATUS } from 'store/constant';
import useApi from 'hooks/useApi';
import VenueService from 'services/VenueService';
import UserService from 'services/UserService';
import bookingFilterSchema from 'utils/schema/BookingfilterSchema';

// third party
import moment from 'moment';
import { useFormik } from 'formik';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

// animation
const Transition = forwardRef((props, ref) => <Slide direction="left" ref={ref} {...props} />);

const Filter = ({ filterObj, open, handleCloseDialog, setFilterObj, viewType, setPage }) => {

    const [venueArray, setVenueArray] = useState([]);
    const [userArray, setUserArray] = useState([]);

    const filterCourtBookings = () => {
        const formikValues = { ...formik.values };
        if (formikValues.start_date) {
            formikValues.start_date = moment(formikValues.start_date, 'ddd MMM DD YYYY').format('YYYY-MM-DD')
        }
        if (formikValues.end_date) {
            formikValues.end_date = moment(formikValues.end_date, 'ddd MMM DD YYYY').format('YYYY-MM-DD')
        }
        setFilterObj(formikValues);
        setPage(1);
        handleCloseDialog();
    };

    const resetFilterForm = () => {
        formik.resetForm();
        setFilterObj({
            start_date: null,
            end_date: null,
            uuid_user: [],
            uuid_venue: [],
            booking_status: [],
            payment_status: [],
            payment_mode: [],
            payment_type: [],
            deleted: []
        });
        setPage(1);
    };

    const venueListHandler = (response) => {
        setVenueArray(response.data);
    };

    const userListHandler = (response) => {
        setUserArray(response.data);
    };

    const getVenueListApi = useApi(VenueService.getVenueDropdownList, venueListHandler);
    const getUserListApi = useApi(UserService.getDropdownUser, userListHandler);

    const formik = useFormik({
        initialValues: {
            start_date: null,
            end_date: null,
            uuid_user: [],
            uuid_venue: viewType ? [filterObj.uuid_venue] : [],
            booking_status: [],
            payment_status: [],
            payment_mode: [],
            payment_type: [],
            deleted: []
        },
        validationSchema: bookingFilterSchema,
        onSubmit: filterCourtBookings
    });

    const setFormValue = () => {
        formik.setFieldValue('start_date', filterObj.start_date ? moment(filterObj.start_date).format('ddd MMM DD YYYY') : null);
        formik.setFieldValue('end_date', filterObj.end_date ? moment(filterObj.end_date).format('ddd MMM DD YYYY') : null);
        formik.setFieldValue('uuid_venue', filterObj.uuid_venue.length ? filterObj.uuid_venue : []);
        formik.setFieldValue('uuid_user', filterObj.uuid_user.length ? filterObj.uuid_user : []);
        formik.setFieldValue('booking_status', filterObj.booking_status.length ? filterObj.booking_status : []);
        formik.setFieldValue('payment_status', filterObj.payment_status.length ? filterObj.payment_status : []);
        formik.setFieldValue('payment_mode', filterObj.payment_mode.length ? filterObj.payment_mode : []);
        formik.setFieldValue('payment_type', filterObj.payment_type.length ? filterObj.payment_type : []);
        formik.setFieldValue('deleted', filterObj.deleted ? filterObj.deleted : []);
    };

    useEffect(() => {
        getVenueListApi.execute();
        getUserListApi.execute({ type: [USER_TYPE[1].value] });
        if (filterObj) {
            setFormValue();
        }
    }, []);

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleCloseDialog}
            sx={{
                '&>div:nth-of-type(3)': {
                    justifyContent: 'flex-end',
                    '&>div': {
                        m: 0,
                        borderRadius: '0px',
                        maxWidth: 450,
                        maxHeight: '100%',
                        height: '100%'
                    }
                }
            }}
        >
            {open && (
                <>
                    <DialogTitle>Filter</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={gridSpacing} sx={{ mt: 0.25 }}>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Start Date"
                                        views={["year", "month", "day"]}
                                        inputFormat="dd-MM-yyyy"
                                        onChange={(date) => formik.setFieldValue("start_date", date ? date : "")}
                                        onBlur={formik.handleBlur}
                                        // disableFuture
                                        value={formik.values.start_date}
                                        renderInput={(params) => <TextField fullWidth name="start_date" onBlur={formik.handleBlur} autoComplete="off"
                                            value={formik.values.start_date} onChange={formik.handleChange} onKeyDown={(e) => { e.preventDefault(); }} size="medium" {...params} />}
                                    />
                                </LocalizationProvider>
                                {formik.touched.start_date && formik.errors.start_date && (
                                    <FormHelperText error id="standard-weight-helper-date-start_date-filter">
                                        {formik.errors.start_date}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="End Date"
                                        views={["year", "month", "day"]}
                                        inputFormat="dd-MM-yyyy"
                                        onChange={(date) => formik.setFieldValue("end_date", date ? date : "")}
                                        onBlur={formik.handleBlur}
                                        // disableFuture
                                        value={formik.values.end_date}
                                        renderInput={(params) => <TextField fullWidth name="end_date" onBlur={formik.handleBlur} autoComplete="off"
                                            value={formik.values.end_date} onChange={formik.handleChange} onKeyDown={(e) => { e.preventDefault(); }} size="medium" {...params} />}
                                    />
                                </LocalizationProvider>
                                {formik.touched.end_date && formik.errors.end_date && (
                                    <FormHelperText error id="standard-weight-helper-date-end_date-filter">
                                        {formik.errors.end_date}
                                    </FormHelperText>
                                )}
                            </Grid>
                            {
                                viewType ? (<></>) : (
                                    <Grid item xs={12}>
                                        <FormControl captionLabel='Venue' fullWidth size="medium">
                                            <InputLabel id="venue-select-label">Venue</InputLabel>
                                            <Select
                                                labelId="venue-select-label"
                                                id="venue-select"
                                                label="venue"
                                                name="uuid_venue"
                                                size="medium"
                                                multiple
                                                error={Boolean(formik.touched.uuid_venue && formik.errors.uuid_venue)}
                                                value={formik.values.uuid_venue}
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                            >
                                                {
                                                    venueArray.map((venueObj, index) => {
                                                        return (
                                                            <MenuItem key={index} value={venueObj.uuid}>{venueObj.venue_name}</MenuItem>
                                                        )
                                                    })
                                                }
                                            </Select>
                                        </FormControl>
                                        {formik.touched.uuid_venue && formik.errors.uuid_venue && (
                                            <FormHelperText error id="standard-weight-helper-text-uuid_venue-filter">
                                                {formik.errors.uuid_venue}
                                            </FormHelperText>
                                        )}
                                    </Grid>
                                )
                            }
                            <Grid item xs={12}>
                                <FormControl captionLabel='User' fullWidth size="medium">
                                    <InputLabel id="user-select-label">User</InputLabel>
                                    <Select
                                        labelId="user-select-label"
                                        id="user-select"
                                        label="user"
                                        name="uuid_user"
                                        multiple
                                        error={Boolean(formik.touched.uuid_user && formik.errors.uuid_user)}
                                        value={formik.values.uuid_user}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        MenuProps={MenuProps}
                                    >
                                        {
                                            userArray.map((userObj, index) => {
                                                return (
                                                    <MenuItem key={index} value={userObj.uuid}>{userObj.firstname} {userObj.lastname}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.uuid_user && formik.errors.uuid_user && (
                                    <FormHelperText error id="standard-weight-helper-text-uuid_user-filter">
                                        {formik.errors.uuid_user}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl captionLabel='Booking Status' fullWidth size="medium">
                                    <InputLabel id="booking-status-select-label">Booking Status</InputLabel>
                                    <Select
                                        labelId="booking-status-select-label"
                                        id="booking-status-select"
                                        label="booking-status"
                                        name="booking_status"
                                        multiple
                                        error={Boolean(formik.touched.booking_status && formik.errors.booking_status)}
                                        value={formik.values.booking_status}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    >
                                        {
                                            BOOKING_STATUS.map((bookingStatus, index) => {
                                                return (
                                                    <MenuItem key={index} value={bookingStatus.value}>{bookingStatus.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.booking_status && formik.errors.booking_status && (
                                    <FormHelperText error id="standard-weight-helper-text-booking-status-filter">
                                        {formik.errors.booking_status}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl captionLabel='Payment Status' fullWidth size="medium">
                                    <InputLabel id="payment-status-select-label">Payment Status</InputLabel>
                                    <Select
                                        labelId="payment-status-select-label"
                                        id="payment-status-select"
                                        label="payment-status"
                                        name="payment_status"
                                        multiple
                                        error={Boolean(formik.touched.payment_status && formik.errors.payment_status)}
                                        value={formik.values.payment_status}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    >
                                        {
                                            PAYMENT_STATUS.map((paymentStatus, index) => {
                                                return (
                                                    <MenuItem key={index} value={paymentStatus.value}>{paymentStatus.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.payment_status && formik.errors.payment_status && (
                                    <FormHelperText error id="standard-weight-helper-text-payment-status-filter">
                                        {formik.errors.payment_status}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl captionLabel='Payment Mode' fullWidth size="medium">
                                    <InputLabel id="payment-mode-select-label">Payment Mode</InputLabel>
                                    <Select
                                        labelId="payment-mode-select-label"
                                        id="payment-mode-select"
                                        label="payment-mode"
                                        name="payment_mode"
                                        multiple
                                        error={Boolean(formik.touched.payment_mode && formik.errors.payment_mode)}
                                        value={formik.values.payment_mode}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    >
                                        {
                                            PAYMENT_MODE.map((paymentMode, index) => {
                                                return (
                                                    <MenuItem key={index} value={paymentMode.value}>{paymentMode.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.payment_mode && formik.errors.payment_mode && (
                                    <FormHelperText error id="standard-weight-helper-text-payment-mode-filter">
                                        {formik.errors.payment_mode}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl captionLabel='Payment Type' fullWidth size="medium">
                                    <InputLabel id="payment-type-select-label">Payment Type</InputLabel>
                                    <Select
                                        labelId="payment-type-select-label"
                                        id="payment-type-select"
                                        label="payment-type"
                                        name="payment_type"
                                        multiple
                                        error={Boolean(formik.touched.payment_type && formik.errors.payment_type)}
                                        value={formik.values.payment_type}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    >
                                        {
                                            PAYMENT_TYPE.map((paymentType, index) => {
                                                return (
                                                    <MenuItem key={index} value={paymentType.value}>{paymentType.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.payment_type && formik.errors.payment_type && (
                                    <FormHelperText error id="standard-weight-helper-text-payment-type-filter">
                                        {formik.errors.payment_type}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl captionLabel='Status' fullWidth size="medium">
                                    <InputLabel id="deleted-select-label">Status</InputLabel>
                                    <Select
                                        labelId="deleted-select-label"
                                        id="deleted-select"
                                        label="deleted"
                                        name="deleted"
                                        error={Boolean(formik.touched.deleted && formik.errors.deleted)}
                                        value={formik.values.deleted}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        multiple
                                    >
                                        {
                                            DELETED_STATUS.map((deleted_status, index) => {
                                                return (
                                                    <MenuItem key={index} value={deleted_status.value}>{deleted_status.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.deleted && formik.errors.deleted && (
                                    <FormHelperText error id="standard-weight-helper-text-status-type-filter">
                                        {formik.errors.deleted}
                                    </FormHelperText>
                                )}
                            </Grid>
                        </Grid>
                        <Grid container spacing={gridSpacing} sx={{ mt: 3, justifyContent: 'right' }}>
                            <Button variant="contained" color="inherit" onClick={handleCloseDialog} sx={{ marginRight: '10px' }}>
                                Close
                            </Button>
                            <Button variant="contained" color="error" onClick={resetFilterForm} sx={{ marginRight: '10px' }}>
                                Reset
                            </Button>
                            <Button variant="contained" color="primary" onClick={formik.handleSubmit}>
                                Filter
                            </Button>
                        </Grid>
                    </DialogContent>
                </>
            )}
        </Dialog>
    );
};

export default Filter;
